import {
  OnboardingContext,
  OnboardingContextProps,
} from '@psl/flows/onboarding/OnboardingContext';
import { userProfileDoc } from '@psl/functions/firebase/refs/profileDocRef';
import {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Platform } from 'react-native';
import { BehaviorSubject } from 'rxjs';
import { currentUserId } from '../../functions/auth/utils';
import { useCreation } from '../../functions/hooks/useCreation';
import { usePublicConfig } from '../../functions/hooks/usePublicConfig';
import { useLogout } from '../../functions/user/sessionTracker';
import { AnalyticEvents } from '../../generated/AnalyticEvents';
import { makeDebug } from '../../utils/makeDebug';
import { safeMarkDevFlag } from '../../utils/safeMarkDevFlag';
import { OnboardingUserData } from './functions/types';
import {
  OnboardingProgressInfo,
  useOnboardingProgress,
} from './functions/useOnboardingProgress';

const debug = makeDebug('app:onboarding:context');
const authForMobileDisabled = safeMarkDevFlag(false);

const initialUserData: OnboardingUserData = {
  firstName: '',
  lastName: '',
  email: '',
  username: '',
};

export const DefaultOnboardingProvider = memo(
  ({ children }: { children?: ReactNode }) => {
    const logout = useLogout();
    const { progress, updateProgress } = useOnboardingProgress();

    const [initialUserId, setInitialUserId] = useState(currentUserId());
    const initialized = useRef(false);

    // force user to login on mobile devices
    const publicConfig = usePublicConfig();
    const authenticationRequired =
      Platform.OS !== 'web' &&
      publicConfig.auth?.required_for_mobile !== false &&
      !authForMobileDisabled;

    const [initialProgress, setInitialProgress] =
      useState<OnboardingProgressInfo | null>();

    const userData$ = useCreation(
      () => new BehaviorSubject<OnboardingUserData>(initialUserData),
      [],
    );

    useEffect(() => {
      if (authenticationRequired) {
        debug(
          'new onboarding user experience is matched for the current session',
        );
      } else {
        debug(
          'new onboarding user experience is **NOT** matched for the current session',
        );
      }
    }, [authenticationRequired]);

    useEffect(() => {
      if (
        !authenticationRequired ||
        initialProgress !== undefined ||
        progress === undefined ||
        initialized.current // ensure initialized only once
      ) {
        return;
      }

      initialized.current = true;

      debug(
        'initial progress loaded: ',
        JSON.stringify(progress),
        ', initial user id:',
        initialUserId,
      );

      if (initialUserId != null) {
        // check if user's profile exists.
        debug('checking if user profile exists... ');
        userProfileDoc(initialUserId)
          .get({ source: 'cache' })
          .then(snapshot => {
            debug('user profile snapshot loaded, exists:', snapshot.exists);
            if (
              !snapshot.exists &&
              (progress == null || progress.phase.type === 'auth')
            ) {
              debug('logout user because profile does not exist');
              logout().finally(() => {
                setInitialProgress(progress);
              });
            } else {
              setInitialProgress(progress);
            }
          })
          .catch(e => {
            console.error(e);
            setInitialProgress(progress);
          });
      } else {
        setInitialProgress(progress);
      }
    }, [
      authenticationRequired,
      initialProgress,
      initialUserId,
      logout,
      progress,
    ]);

    const reset = useCallback(() => {
      setInitialProgress(null);
      setInitialUserId(currentUserId());
      userData$.next(initialUserData);
    }, [userData$]);

    const value = useMemo<OnboardingContextProps>(
      () => ({
        authenticationRequired,
        initialUserId,
        initialProgress: initialProgress ?? null,
        userData$,
        reset,
      }),
      [
        authenticationRequired,
        initialUserId,
        initialProgress,
        userData$,
        reset,
      ],
    );

    useEffect(() => {
      if (
        initialProgress != null &&
        initialProgress.phase.type === 'extra-info'
      ) {
        debug('onboarding completed from resume');
        updateProgress(undefined);
        AnalyticEvents.default.newOnboardingSignupCompleted({
          type: 'resumed',
        });
      }
    }, [initialProgress, updateProgress]);

    // wait for the onboarding progress information being loaded
    // if authentication is required.
    if (
      authenticationRequired &&
      (progress === undefined || initialProgress === undefined)
    ) {
      return null;
    }

    return (
      <OnboardingContext.Provider value={value}>
        {children}
      </OnboardingContext.Provider>
    );
  },
);

export default DefaultOnboardingProvider;
